import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import styles from '../MifidRelatedData.module.css';
import CustomerContext from '../../../../context/customer-context';
import { MifidProduct } from '../../../../models/CustomerMifidCategory';
import CustomerMifidAppropriateness, {
  AppropriatenessResult,
} from '../../../../models/CustomerMifidAppropriateness';
import APIError from '../../../../services/APIError';
import {
  deleteAppropriateness,
  getAppropriatenessForCustomer,
  saveAppropriateness,
} from '../../../../services/mifidAppropriateness/mifidAppropriatenessService';
import Select from '../../../common/Select/Select';

function AppropriatenessTest() {
  const { t } = useTranslation();
  const customerCtx = useContext(CustomerContext);
  const getInitMifidAppropriateness = (product: MifidProduct): CustomerMifidAppropriateness => ({
    person: customerCtx.customer,
    product,
    appropriatenessResult: 'Not tested',
  });
  const [appropriateness, setAppropriateness] = useState<CustomerMifidAppropriateness[]>([]);
  const [bond, setBonds] = useState<CustomerMifidAppropriateness>(
    getInitMifidAppropriateness('BONDS'),
  );
  const [shares, setShares] = useState<CustomerMifidAppropriateness>(
    getInitMifidAppropriateness('SHARES'),
  );
  const [fundsEtf, setFundsEtf] = useState<CustomerMifidAppropriateness>(
    getInitMifidAppropriateness('FUNDS_ETF'),
  );
  const [complexFund, setComplexFund] = useState<CustomerMifidAppropriateness>(
    getInitMifidAppropriateness('COMPLEX_FUND'),
  );
  const [otherComplex, setOtherComplex] = useState<CustomerMifidAppropriateness>(
    getInitMifidAppropriateness('COMPLEX_OTHER'),
  );
  const [isEdit, setIsEdit] = useState(false);

  const getAppropriateness = async () => {
    try {
      const result = await getAppropriatenessForCustomer({ id: customerCtx.customer.id });
      setAppropriateness(result);
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const findAppropriateness = (product: MifidProduct) => appropriateness.find(
    element => element.product === product,
  );

  useEffect(() => {
    getAppropriateness();
  }, [customerCtx.customer.id]);

  useEffect(() => {
    setBonds(findAppropriateness('BONDS') ||
      getInitMifidAppropriateness('BONDS'));
    setShares(findAppropriateness('SHARES') ||
      getInitMifidAppropriateness('SHARES'));
    setFundsEtf(findAppropriateness('FUNDS_ETF') ||
      getInitMifidAppropriateness('FUNDS_ETF'));
    setComplexFund(findAppropriateness('COMPLEX_FUND') ||
      getInitMifidAppropriateness('COMPLEX_FUND'));
    setOtherComplex(findAppropriateness('COMPLEX_OTHER') ||
      getInitMifidAppropriateness('COMPLEX_OTHER'));
  }, [appropriateness]);

  const resultValues = [
    { value: 'Not tested' },
    { value: 'YES' },
    { value: 'NO' },
  ];

  const saveMifidAppropriateness = async (mifidAppropriateness: CustomerMifidAppropriateness) => {
    try {
      const existedAppropriateness = findAppropriateness(mifidAppropriateness.product);
      if (existedAppropriateness &&
        existedAppropriateness.appropriatenessResult === mifidAppropriateness.appropriatenessResult
      ) {
        return;
      }
      await saveAppropriateness({ ...mifidAppropriateness });
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const deleteMifidAppropriateness = async (mifidAppropriateness: CustomerMifidAppropriateness) => {
    try {
      if (mifidAppropriateness.id) {
        await deleteAppropriateness({ appropriatenessId: mifidAppropriateness.id! });
      }
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const saveOrDelete = (mifidAppropriateness: CustomerMifidAppropriateness) => {
    if (mifidAppropriateness.appropriatenessResult) {
      if (mifidAppropriateness.appropriatenessResult === 'Not tested') {
        deleteMifidAppropriateness(mifidAppropriateness);
      } else {
        saveMifidAppropriateness(mifidAppropriateness);
      }
    }
  };

  const updateAppropriateness = (product: MifidProduct,
                                 appropriatenessResult: AppropriatenessResult) => {
    switch (product) {
      case 'BONDS':
        setBonds({ ...bond, appropriatenessResult });
        break;
      case 'SHARES':
        setShares({ ...shares, appropriatenessResult });
        break;
      case 'FUNDS_ETF':
        setFundsEtf({ ...fundsEtf, appropriatenessResult });
        break;
      case 'COMPLEX_FUND':
        setComplexFund({ ...complexFund, appropriatenessResult });
        break;
      case 'COMPLEX_OTHER':
        setOtherComplex({ ...otherComplex, appropriatenessResult });
        break;
      default:
        console.log('Update category failed');
    }
  };

  const handleSaveButton = () => {
    saveOrDelete(bond);
    saveOrDelete(shares);
    saveOrDelete(fundsEtf);
    saveOrDelete(complexFund);
    saveOrDelete(otherComplex);
    setIsEdit(false);
    setTimeout(() => {
      getAppropriateness();
    }, 500);
  };

  const handleCancelButton = () => {
    setIsEdit(false);
    setBonds(findAppropriateness('BONDS') ||
      getInitMifidAppropriateness('BONDS'));
    setShares(findAppropriateness('SHARES') ||
      getInitMifidAppropriateness('SHARES'));
    setFundsEtf(findAppropriateness('FUNDS_ETF') ||
      getInitMifidAppropriateness('FUNDS_ETF'));
    setComplexFund(findAppropriateness('COMPLEX_FUND') ||
      getInitMifidAppropriateness('COMPLEX_FUND'));
    setOtherComplex(findAppropriateness('COMPLEX_OTHER') ||
      getInitMifidAppropriateness('COMPLEX_OTHER'));
  };

  return (
    <>
      <div className={styles.buttonContainer}>
        {!isEdit && (
          <button
            type='button'
            className={styles.button}
            onClick={() => setIsEdit(true)}
          >
            {t('common.edit')}
          </button>
        )}
        {isEdit && (
          <div>
            <button
              type='button'
              className={styles.button}
              onClick={() => handleSaveButton()}
            >
              {t('common.save')}
            </button>
            <button
              type='button'
              className={styles.button}
              onClick={() => handleCancelButton()}
            >
              {t('common.cancel')}
            </button>
          </div>
        )}
      </div>
      <div className={classNames(styles.container, isEdit && styles.editContainer)}>
        <div>
          <div className={styles.label}>
            {t('customer.dashboard.mifid.appropriateness')}
          </div>
          {!isEdit && (
            <div className={styles.instruments}>
              <div className={styles.instrument}>
                <div className={styles.label}>
                  {t('customer.dashboard.mifid.bonds')}
                </div>
                <div>{bond.appropriatenessResult}</div>
              </div>
              <div className={styles.instrument}>
                <div className={styles.label}>
                  {t('customer.dashboard.mifid.shares')}
                </div>
                <div>{shares.appropriatenessResult}</div>
              </div>
              <div className={styles.instrument}>
                <div className={styles.label}>
                  {t('customer.dashboard.mifid.fundsEtf')}
                </div>
                <div>{fundsEtf.appropriatenessResult}</div>
              </div>
              <div className={styles.instrument}>
                <div className={styles.label}>
                  {t('customer.dashboard.mifid.complexFund')}
                </div>
                <div>{complexFund.appropriatenessResult}</div>
              </div>
              <div className={styles.instrument}>
                <div className={styles.label}>
                  {t('customer.dashboard.mifid.complexOther')}
                </div>
                <div>{otherComplex.appropriatenessResult}</div>
              </div>
            </div>
          )}
          {isEdit && (
            <div className={styles.instruments}>
              <div className={styles.instrument}>
                <Select
                  id='bonds'
                  options={resultValues}
                  label={t('customer.dashboard.mifid.bonds')}
                  required
                  value={bond.appropriatenessResult}
                  width='285px'
                  onUpdate={(value) => updateAppropriateness('BONDS', value)}
                />
              </div>
              <div className={styles.instrument}>
                <Select
                  id='shares'
                  options={resultValues}
                  label={t('customer.dashboard.mifid.shares')}
                  required
                  value={shares.appropriatenessResult}
                  width='285px'
                  onUpdate={(value) => updateAppropriateness('SHARES', value)}
                />
              </div>
              <div className={styles.instrument}>
                <Select
                  id='fundsEtf'
                  options={resultValues}
                  label={t('customer.dashboard.mifid.fundsEtf')}
                  required
                  value={fundsEtf.appropriatenessResult}
                  width='285px'
                  onUpdate={(value) => updateAppropriateness('FUNDS_ETF', value)}
                />
              </div>
              <div className={styles.instrument}>
                <Select
                  id='complex_funds'
                  options={resultValues}
                  label={t('customer.dashboard.mifid.complexFund')}
                  isLongLabel
                  required
                  value={complexFund.appropriatenessResult}
                  width='285px'
                  onUpdate={(value) => updateAppropriateness('COMPLEX_FUND', value)}
                />
              </div>
              <div className={styles.instrument}>
                <Select
                  id='complexOther'
                  options={resultValues}
                  label={t('customer.dashboard.mifid.complexOther')}
                  isLongLabel
                  required
                  value={otherComplex.appropriatenessResult}
                  width='285px'
                  onUpdate={(value) => updateAppropriateness('COMPLEX_OTHER', value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AppropriatenessTest;
